/**
 * Pluggable implied-volatility solver.
 *
 * This is the seam: callers inject a pricing function (Black-Scholes premium as
 * a function of sigma) and pick an engine — `bisection` (default, matches the
 * historical behavior), `newton`, or `brent`. Swapping engines is a one-word
 * change, which is exactly what lets us A/B bisection vs Brent on real data
 * without touching call sites.
 *
 * All three engines share the same no-arbitrage gate and bracket, so they are
 * directly comparable. See `./rootFinders` for the underlying numerics and
 * `./__tests__` for the round-trip / cross-engine equivalence proofs.
 */
import { bisection, newton, brent, RootFinderResult } from './rootFinders';
import { ImpliedVolatilityInput, ImpliedVolatilityResult, IVMethod } from './types';

export { IV_METHODS } from './types';
export type {
  ImpliedVolatilityInput,
  ImpliedVolatilityResult,
  IVMethod,
  IVFailureReason,
} from './types';

const DEFAULTS = {
  volLow: 1e-4, // 0.01%
  volHigh: 5.0, // 500% — wide enough for 0DTE / meme / biotech names. The old
  // 100% ceiling silently mis-reported any option whose true IV exceeded it.
  priceTolerance: 1e-4,
  volTolerance: 1e-8,
  maxIterations: 100,
  initialGuess: 0.2,
};

const ARB_EPSILON = 1e-9;

export function solveImpliedVolatility(
  input: ImpliedVolatilityInput,
): ImpliedVolatilityResult {
  const method: IVMethod = input.method ?? 'bisection';
  const volLow = input.volLow ?? DEFAULTS.volLow;
  const volHigh = input.volHigh ?? DEFAULTS.volHigh;
  const priceTolerance = input.priceTolerance ?? DEFAULTS.priceTolerance;
  const volTolerance = input.volTolerance ?? DEFAULTS.volTolerance;
  const maxIterations = input.maxIterations ?? DEFAULTS.maxIterations;
  const initialGuess = input.initialGuess ?? DEFAULTS.initialGuess;

  const fail = (reason: ImpliedVolatilityResult['reason']): ImpliedVolatilityResult => ({
    impliedVolatility: NaN,
    iterations: 0,
    converged: false,
    method,
    reason,
  });

  // No-arbitrage gate — only enforced when bounds are supplied. A price below
  // intrinsic (or above the ceiling) has no implied vol; surfacing that beats
  // returning a plausible-looking number for bad data.
  if (input.intrinsic != null && input.marketPrice < input.intrinsic - ARB_EPSILON) {
    return fail('below-intrinsic');
  }
  if (input.priceCeiling != null && input.marketPrice > input.priceCeiling + ARB_EPSILON) {
    return fail('above-ceiling');
  }

  // Solve f(sigma) = modelPrice(sigma) - marketPrice = 0.
  const f = (sigma: number) => input.priceFn(sigma) - input.marketPrice;

  let result: RootFinderResult;
  switch (method) {
    case 'newton': {
      if (!input.vegaFn) return fail('missing-vega-fn');
      // f'(sigma) = d(price)/d(sigma) = vega (raw, un-scaled).
      result = newton(f, input.vegaFn, initialGuess, {
        fTolerance: priceTolerance,
        maxIterations,
        lower: volLow,
        upper: volHigh,
      });
      break;
    }
    case 'brent':
      result = brent(f, volLow, volHigh, {
        fTolerance: priceTolerance,
        xTolerance: volTolerance,
        maxIterations,
      });
      break;
    case 'bisection':
    default:
      result = bisection(f, volLow, volHigh, {
        fTolerance: priceTolerance,
        xTolerance: volTolerance,
        maxIterations,
      });
      break;
  }

  if (!result.converged) {
    // A NaN root from a bracketing method means the root wasn't inside [volLow,
    // volHigh] (true IV outside the search range); otherwise we ran out of
    // iterations.
    const reason = Number.isNaN(result.root) ? 'not-bracketed' : 'no-convergence';
    return {
      impliedVolatility: result.root,
      iterations: result.iterations,
      converged: false,
      method,
      reason,
    };
  }

  return {
    impliedVolatility: result.root,
    iterations: result.iterations,
    converged: true,
    method,
  };
}
